import { useCallback, useEffect, useRef, useState } from 'react';
import {
  v2SessionInfo,
  v2GetDivisions,
  DivisionMembership,
} from 'src/store/modules/auth/v2Operations';
import {
  getDivisions,
  getCurrentDivisionId,
  saveDivisions,
  CURRENT_DIVISION_KEY
} from 'src/utils/divisionStorage';

const DISMISSED_MISMATCH_KEY = 'sl-division-mismatch-dismissed';

function isDismissedMismatch(divisionId: string): boolean {
  try { return sessionStorage.getItem(DISMISSED_MISMATCH_KEY) === divisionId; } catch { return false; }
}

function setDismissedMismatch(divisionId: string): void {
  try { sessionStorage.setItem(DISMISSED_MISMATCH_KEY, divisionId); } catch { /* ignore */ }
}

export function clearDismissedMismatch(): void {
  try { sessionStorage.removeItem(DISMISSED_MISMATCH_KEY); } catch { /* ignore */ }
}

interface UseDivisionMismatchReturn {
  hasMismatch: boolean;
  mismatchDivisionId: string | null;
  mismatchDivisionName: string | null;
  divisions: DivisionMembership[] | null;
  activeDivisionId: string | null;
  dismissMismatch: () => void;
}

/**
 * Detects cross-tab division changes via localStorage storage events
 * and visibilitychange + /v2/session-info. Shared between
 * AccountDivisionPopover and Omni's sidebar/profile UI.
 *
 * Replaces the previous /v2/validate-on-focus pattern with the lighter
 * session-info call. Divisions list is fetched once on mount via
 * /v2/divisions; the focus path doesn't refresh it. See
 * docs/auth-v2-followups.md (followup #0) in the api repo.
 */
export function useDivisionMismatch(currentDivisionId: string | null | undefined): UseDivisionMismatchReturn {
  const [hasMismatch, setHasMismatch] = useState(false);
  const [mismatchDivisionId, setMismatchDivisionId] = useState<string | null>(null);
  const [mismatchDivisionName, setMismatchDivisionName] = useState<string | null>(null);
  const [divisions, setDivisions] = useState<DivisionMembership[] | null>(() => getDivisions());
  const [activeDivisionId, setActiveDivisionId] = useState<string | null>(() => getCurrentDivisionId());
  const detectedRef = useRef(false);

  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const [sessionInfo, divisionsResp] = await Promise.all([
          v2SessionInfo(),
          v2GetDivisions().catch(() => null),
        ]);
        if (sessionInfo.authenticated && divisionsResp?.divisions?.length) {
          saveDivisions(divisionsResp.divisions);
          setDivisions(divisionsResp.divisions);
          if (sessionInfo.divisionId) {
            setActiveDivisionId(sessionInfo.divisionId);
          }
        }
      } catch (error) {
        console.error('[useDivisionMismatch] Failed to fetch divisions:', error);
      }
    };
    fetchDivisions();
  }, []);

  useEffect(() => {
    const effectiveDivisionId = currentDivisionId || activeDivisionId;

    const triggerMismatch = (newId: string, newName: string) => {
      setHasMismatch(true);
      setMismatchDivisionId(newId);
      setMismatchDivisionName(newName);
      detectedRef.current = true;
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== CURRENT_DIVISION_KEY) return;
      const newDivisionId = e.newValue;
      if (newDivisionId && newDivisionId !== effectiveDivisionId && !isDismissedMismatch(newDivisionId)) {
        const cached = getDivisions();
        const newDiv = cached?.find((d) => d.id === newDivisionId);
        triggerMismatch(newDivisionId, newDiv?.companyName ? `${newDiv.companyName} ${newDiv.name}` : (newDiv?.name || newDivisionId));
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!effectiveDivisionId) {
        return;
      }
      try {
        // Lightweight session-info check: only the divisionId is returned.
        // Look up the new division's display name from the localStorage cache
        // (populated on mount); fall back to bare id when uncached.
        const response = await v2SessionInfo();
        if (!response.authenticated || !response.divisionId) return;
        if (response.divisionId !== effectiveDivisionId && !isDismissedMismatch(response.divisionId)) {
          const cached = getDivisions();
          const newDiv = cached?.find(d => d.id === response.divisionId);
          triggerMismatch(
            response.divisionId,
            newDiv?.companyName
              ? `${newDiv.companyName} ${newDiv.name}`
              : (newDiv?.name || response.divisionId),
          );
        }
      } catch (err) {
        console.error('[useDivisionMismatch] session-info error:', err);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentDivisionId, activeDivisionId]);

  const dismissMismatch = useCallback(() => {
    if (mismatchDivisionId) {
      setDismissedMismatch(mismatchDivisionId);
    }
    setHasMismatch(false);
    setMismatchDivisionId(null);
    setMismatchDivisionName(null);
  }, [mismatchDivisionId]);

  return {
    hasMismatch,
    mismatchDivisionId,
    mismatchDivisionName,
    divisions,
    activeDivisionId,
    dismissMismatch
  };
}

export default useDivisionMismatch;
